import {
  Input,
  InputGroup,
  InputGroupLabel,
  Label,
  Message,
} from '@sbab-ui/components'
import { InfoIcon } from '@sbab-ui/icons'
import { NumberFormatValues, NumericFormat } from 'react-number-format'
import CustomSlider from './CustomSlider'
import { EventUnionType } from './MortgageCalculator'

export interface IMessage {
  tone: 'critical' | 'positive'
  text: string
}

interface Props {
  value: number
  label: string
  suffix?: string
  id:
  | 'propertyValue'
  | 'downPayment'
  | 'monthlyFee'
  | 'yearlyOperatingCost'
  | 'personalLoanAmount'
  inputChangeHandler: (event: EventUnionType) => void
  statusMessage?: IMessage | undefined
  isValidHandler?: (value: number) => boolean
  maxLength: number
  useSlider?: boolean
  min?: number
  max?: number
  sliderStep?: number
  sliderMinMark?: number | boolean | readonly number[] | undefined
  additionalInformationMessage?: string
  errorMessage?: string
  valueIsAllowed?: (values: NumberFormatValues) => boolean
}

function camelToKebab(camelCaseString: string) {
  return camelCaseString.replace(/[A-Z]/g, (match) => `-${match.toLowerCase()}`)
}

function CustomInput({
  value,
  label,
  suffix,
  id,
  inputChangeHandler,
  isValidHandler,
  maxLength,
  useSlider = false,
  min = 0,
  max = 0,
  sliderStep = 1000,
  sliderMinMark,
  statusMessage = undefined,
  additionalInformationMessage = undefined,
  errorMessage = '',
  valueIsAllowed,
}: Readonly<Props>) {
  let hasError = false
  if (isValidHandler !== undefined) {
    hasError = !isValidHandler(value)
  }

  return (
    <div data-cs-mask className="mb-2 flex w-full flex-col lg:mr-3 lg:max-w-[47%]">
      <Label className="mb-1" htmlFor={id}>
        {label}
      </Label>
      <InputGroup>
        <NumericFormat
          value={value}
          customInput={Input}
          id={id}
          data-label={id}
          thousandSeparator=" "
          onChange={inputChangeHandler}
          maxLength={maxLength}
          invalid={hasError}
          style={{ width: '100%' }}
          isAllowed={valueIsAllowed}
        />
        {suffix ? (
          <InputGroupLabel className="whitespace-nowrap">
            {suffix}
          </InputGroupLabel>
        ) : null}
      </InputGroup>
      {useSlider ? (
        <CustomSlider
          type={id}
          label={label}
          value={value}
          onChangeHandler={inputChangeHandler}
          min={min}
          max={max}
          step={sliderStep}
          marks={sliderMinMark}
        />
      ) : null}
      {statusMessage && !hasError ? (
        <Message
          tone={statusMessage.tone}
          className="mt-2"
          data-cy={`${camelToKebab(id)}-status-msg`}
        >
          {statusMessage.text}
        </Message>
      ) : null}
      {hasError ? (
        <Message
          tone="critical"
          className="mt-2"
          data-cy={`${camelToKebab(id)}-error-msg`}
        >
          {errorMessage}
        </Message>
      ) : null}
      {additionalInformationMessage ? (
        <div className="mt-2 flex">
          <InfoIcon size="24px" color="#FF620F" />
          <span className="w-full">{additionalInformationMessage}</span>
        </div>
      ) : null}
    </div>
  )
}

export default CustomInput
